import { useEffect, useRef, useState } from "react";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";
import { getPublicReviews } from "../services/reviewsService";

// "Stories from DOTORE Learners" — gray section + horizontal scroll-snap row of
// slightly-rotated white testimonial cards (avatar + name, stars, quote, date) per Figma.
// CMS-driven via `stories` ({ name, quote, image, rating, date, meta }); fallback below.
// Localization: title/subtitle = CMS props (Umbraco variant). When CMS empty -> i18n
// translate() defaults (NOT hardcoded English). Default story quotes route through i18n too;
// names/dates/ratings/structure are fixed sample data.
const DEFAULT_STORIES = [
  { name: "Bao Hoang", rating: 4, date: "2026.06.12", quoteKey: "quote1" },
  { name: "Huynh Man Nhi", rating: 4, date: "2026.06.08", quoteKey: "quote2" },
  { name: "Sarah Kim", rating: 5, date: "2026.05.10", quoteKey: "quote3" },
  { name: "Jason Nguyen", rating: 5, date: "2026.06.12", quoteKey: "quote4" },
  { name: "Daniel Vo", rating: 5, date: "2026.04.28", quoteKey: "quote5" },
];
const HOME_STORIES_COUNT = 12; // number of latest public reviews to pull for the marquee

// Format an ISO date to "YYYY.MM.DD" (matches the sample story dates).
const fmtReviewDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
};

const Stars = ({ rating = 5 }) => {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return (
    <div className="review-card__stars" aria-label={`${r} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} className={"review-card__star" + (i < r ? " is-on" : "")} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M11.9969 2C12.9691 2.00017 13.7381 2.72927 14.2303 3.71777L15.868 6.99121L15.8699 6.99707C15.919 7.0971 16.0359 7.2396 16.2127 7.37012C16.3894 7.50049 16.5635 7.57258 16.6785 7.5918L19.6453 8.08496C20.7138 8.26324 21.6142 8.78346 21.907 9.69434C22.1999 10.6061 21.7693 11.5503 20.9998 12.3154L21.0008 12.3164L18.6941 14.623C18.6034 14.7139 18.502 14.8849 18.4383 15.1074C18.3754 15.3271 18.3696 15.5264 18.3973 15.6553L19.0574 18.5107L19.1043 18.7334C19.317 19.845 19.1875 20.9197 18.3943 21.498C17.5495 22.114 16.3958 21.8426 15.3445 21.2217L12.5623 19.5742C12.445 19.505 12.244 19.4493 12.0017 19.4492C11.7595 19.4492 11.5522 19.5047 11.4275 19.5771L8.65213 21.2217L8.65116 21.2207C7.6013 21.8435 6.44905 22.1105 5.60428 21.4941C4.75909 20.8771 4.66342 19.6975 4.93827 18.5098L5.59842 15.6553C5.62605 15.5264 5.62026 15.327 5.55741 15.1074C5.49369 14.8849 5.39223 14.7139 5.30155 14.623L2.99491 12.3164C2.22964 11.5511 1.80077 10.6063 2.09159 9.69531C2.3827 8.78374 3.28095 8.26328 4.35038 8.08496L7.31424 7.5918C7.42386 7.57318 7.59638 7.50219 7.77323 7.37109C7.94972 7.24018 8.06673 7.0973 8.116 6.99707L9.75565 3.71777L9.75663 3.7168C10.2534 2.72838 11.0252 2.00003 11.9969 2Z" />
        </svg>
      ))}
    </div>
  );
};

export default function StoriesSection({ props }) {
  const [lang, setLang] = useState(null);
  const [t, setT] = useState({});
  // Real public learner reviews (rating>=4, newest first) from the Reviews API.
  const [apiReviews, setApiReviews] = useState(null);
  // Seamless infinite marquee: the track is `copies` repetitions of the base list,
  // animated translateX(0 → -50%). For no gap on wide / zoomed-out screens, ONE half
  // of the track must be at least as wide as the container — we bump `copies` to fit.
  const marqueeRef = useRef(null);
  const trackRef = useRef(null);
  const [copies, setCopies] = useState(2);

  // Fetch latest public reviews once on mount. On empty/error, fall back to i18n samples.
  useEffect(() => {
    let active = true;
    getPublicReviews(HOME_STORIES_COUNT)
      .then((list) => { if (active) setApiReviews(Array.isArray(list) ? list : []); })
      .catch(() => { if (active) setApiReviews([]); });
    return () => { active = false; };
  }, []);

  // Init i18n + track current language (re-translate on language change)
  useEffect(() => {
    if (!i18n.isInitialized) {
      initializeI18n().then(() => setLang(i18n.language));
    } else {
      setLang(i18n.language);
    }
  }, []);

  // i18n defaults — used only when the CMS field is empty (no hardcoded English in markup)
  useEffect(() => {
    let active = true;
    (async () => {
      const out = {};
      out.title = await translate("UMBRACO_LANDING.HOME.STORIES.TITLE", false, "Stories from DOTORE Learners");
      out.subtitle = await translate("UMBRACO_LANDING.HOME.STORIES.SUBTITLE", false, "Real learners, real progress, and real outcomes — from TOPIK preparation to studying abroad, speaking, and work.");
      out.avatarAlt = await translate("UMBRACO_LANDING.HOME.STORIES.AVATAR_ALT", false, "learner");
      out.quote1 = await translate("UMBRACO_LANDING.HOME.STORIES.QUOTE1", false, "The variety of live classes makes learning feel alive.");
      out.quote2 = await translate("UMBRACO_LANDING.HOME.STORIES.QUOTE2", false, "The learning materials are well organized, and the platform is very easy to navigate.");
      out.quote3 = await translate("UMBRACO_LANDING.HOME.STORIES.QUOTE3", false, "Every lesson feels engaging and productive.");
      out.quote4 = await translate("UMBRACO_LANDING.HOME.STORIES.QUOTE4", false, "The level test helped me find the right class immediately. I didn't waste time learning things I already knew.");
      out.quote5 = await translate("UMBRACO_LANDING.HOME.STORIES.QUOTE5", false, "The tutors are patient, supportive, and always encourage me.");
      if (active) setT(out);
    })();
    return () => { active = false; };
  }, [lang]);

  // CMS first (Umbraco variant); i18n default when CMS empty
  const title = t.title || props?.storiesTitle || "";
  const subtitle = t.subtitle || props?.storiesSubtitle || "";

  const i18nStories = DEFAULT_STORIES.map((s) => ({
    name: s.name,
    rating: s.rating,
    date: s.date,
    quote: t[s.quoteKey],
  }));

  // Real reviews → story-card shape; falls back to CMS props, then i18n sample stories.
  const apiStories = (apiReviews || []).map((r) => ({
    name: r.learnerName,
    image: r.avatarUrl,
    rating: r.rating,
    quote: r.reviewText,
    date: fmtReviewDate(r.createdTime),
  }));

  const stories =
    apiStories.length > 0
      ? apiStories
      : Array.isArray(props?.stories) && props.stories.length > 0
        ? props.stories
        : i18nStories;

  // Grow `copies` until one half of the track (= copies/2 base sets) is at least
  // as wide as the container. Converges in 2 renders: oneSet is invariant, so the
  // second pass recomputes the same `want` and stops. Re-runs on resize/zoom.
  useEffect(() => {
    const marquee = marqueeRef.current;
    const track = trackRef.current;
    if (!marquee || !track || !stories.length) return;
    const fit = () => {
      const oneSet = track.scrollWidth / copies; // width of the base list once
      if (!oneSet) return;
      const perHalf = Math.max(1, Math.ceil(marquee.clientWidth / oneSet));
      const want = perHalf * 2; // two identical halves -> seamless -50% loop
      setCopies((prev) => (prev === want ? prev : want));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [stories.length, copies]);

  return (
    <section className="stories-section">
      <div className="stories-section__head">
        <h2 className="stories-section__title animate-on-scroll">{title}</h2>
        <p className="stories-section__subtitle animate-on-scroll">{subtitle}</p>
      </div>

      <div className="review-marquee" ref={marqueeRef}>
        <div
          className="review-marquee__track"
          ref={trackRef}
          /* Keep a constant scroll speed: one half = copies/2 base sets, each set
             ~45s, so duration scales with how many sets a half contains. */
          style={{ animationDuration: `${(copies / 2) * 45}s` }}
        >
        {Array.from({ length: copies * stories.length }).map((_, i) => {
          const s = stories[i % stories.length];
          // Tilt by index within one base set, so every set is identical and the
          // two halves match -> the -50% loop wraps with no rotation jump.
          const tilt = (i % stories.length) % 2 === 0 ? "review-card--tilt-a" : "review-card--tilt-b";
          return (
          <article className={"review-card " + tilt} key={i}>
            <div className="review-card__head">
              <div className="review-card__avatar">
                {s.image ? (
                  <img src={s.image} alt={s.name || t.avatarAlt} />
                ) : (
                  <span className="review-card__avatar-fallback" aria-hidden="true">
                    {(s.name || "?").trim().charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <p className="review-card__name">{s.name}</p>
            </div>
            <Stars rating={s.rating != null ? s.rating : 5} />
            {s.quote && <p className="review-card__quote">{s.quote}</p>}
            {(s.date || s.meta) && <p className="review-card__date">{s.date || s.meta}</p>}
          </article>
          );
        })}
        </div>
      </div>
    </section>
  );
}
